import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowRight, Crown } from "lucide-react";
import { BrainIcon, SparklesIcon } from "@/components/icons/CustomIcons";

interface DiagnosticPromptProps {
  onTakeDiagnostic: () => void;
  onSkip: () => void;
  isLoading: boolean;
  /** When true, show a Pro badge on the diagnostic button */
  proLocked?: boolean;
}

export function DiagnosticPrompt({ onTakeDiagnostic, onSkip, isLoading, proLocked }: DiagnosticPromptProps) {
  const { t } = useTranslation();

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ type: "spring", stiffness: 200, damping: 20 }}
    >
      <Card className="border-primary/20 shadow-lg">
        <CardContent className="p-8 text-center space-y-6">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.1, type: "spring", stiffness: 200 }}
          >
            <BrainIcon className="w-12 h-12 mx-auto text-primary" />
          </motion.div>

          <div>
            <h2 className="text-xl font-semibold mb-2">
              {t("diagnostic.title", "Personalize your revision sheet")}
            </h2>
            <p className="text-muted-foreground text-sm max-w-md mx-auto">
              {t("diagnostic.description", "Take a quick 10-question diagnostic to generate a sheet adapted to your level. It only takes a couple of minutes.")}
            </p>
            <p className="text-muted-foreground text-xs max-w-md mx-auto mt-2">
              {t("diagnostic.honestyNote", "Answer as naturally as possible without looking up answers. the more honest your responses, the better your sheet will match your real level. You can always adjust later.")}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              onClick={onTakeDiagnostic}
              disabled={isLoading}
              className="gap-2"
              size="lg"
              variant={proLocked ? "outline" : "default"}
            >
              <BrainIcon className="w-4 h-4" />
              {proLocked
                ? t("pro.unlockDiagnostic", "Unlock with Pro")
                : t("diagnostic.takeDiagnostic", "Take the diagnostic")}
              {proLocked ? <Crown className="w-4 h-4" /> : <SparklesIcon className="w-4 h-4" />}
            </Button>
            <Button
              variant="outline"
              onClick={onSkip}
              disabled={isLoading}
              className="gap-2"
              size="lg"
            >
              {t("diagnostic.skip", "Skip. Standard level")}
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>

          <p className="text-xs text-muted-foreground">
            {t("diagnostic.recommended", "Recommended for a personalized experience")}
          </p>
        </CardContent>
      </Card>
    </motion.div>
  );
}
